import { useRef, useState } from 'react';
import {
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';

import { RestaurantComboDTO } from '../../../../types/restaurant/RestaurantCatalogExtensionTypes';
import {
  RestaurantMenuCategoryDTO,
  RestaurantMenuItemDTO,
} from '../../../../types/restaurant/RestaurantMenuTypes';
import {
  EMPTY_MENU_FILTERS,
  RestaurantMenuAvailabilityFilter,
  RestaurantOrderMenuFilters,
  countActiveMenuFilters,
} from '../../../../types/restaurant/RestaurantOrderTypes';
import { formatOrderMoney } from '../orderPresentation';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';
import CompactSearchSelect from '../../../../components/Forms/Input/CompactSearchSelect';
import CompactInputField from '../../../../components/Forms/Input/CompactInputField';
import Checkbox from '../../../../components/Forms/Input/Checkbox';
import ActionButton from '../../../../components/Buttons/ActionButton';
import InputIconByKey from '../../../../components/Forms/Input/InputIconByKey';
import RestaurantMenuItemImage from './RestaurantMenuItemImage';
import { IconKey } from '../../../../types/system/icons';

interface OrderMenuPanelProps {
  categories: RestaurantMenuCategoryDTO[];
  menuItems: RestaurantMenuItemDTO[];
  combos: RestaurantComboDTO[];
  selectedCategoryId: number | 'ALL';
  search: string;
  filters: RestaurantOrderMenuFilters;
  menuLoading?: boolean;
  canEdit: boolean;
  busy: boolean;
  onSearchChange: (value: string) => void;
  onFiltersChange: (filters: RestaurantOrderMenuFilters) => void;
  onCategoryChange: (categoryId: number | 'ALL') => void;
  onChooseItem: (item: RestaurantMenuItemDTO) => void;
  onChooseCombo: (combo: RestaurantComboDTO) => void;
}

const AVAILABILITY_OPTIONS: {
  value: RestaurantMenuAvailabilityFilter;
  label: string;
}[] = [
  { value: 'AVAILABLE', label: 'Disponibles' },
  { value: 'ALL', label: 'Todos' },
  { value: 'UNAVAILABLE', label: 'No disp.' },
];

const OrderMenuPanel = ({
  categories,
  menuItems,
  combos,
  selectedCategoryId,
  search,
  filters,
  menuLoading = false,
  canEdit,
  busy,
  onSearchChange,
  onFiltersChange,
  onCategoryChange,
  onChooseItem,
  onChooseCombo,
}: OrderMenuPanelProps) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = countActiveMenuFilters(filters);

  const scrollStrip = (direction: -1 | 1) => {
    stripRef.current?.scrollBy({ left: direction * 220, behavior: 'smooth' });
  };

  const patchFilters = (patch: Partial<RestaurantOrderMenuFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <section className="min-h-[640px] bg-white dark:bg-boxdark">
      <div className="border-b border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4/20">
        <h2 className="font-semibold text-black dark:text-white">
          Menú disponible
        </h2>

        <p className="mb-3 text-xs text-bodydark2">
          Agregue platos, bebidas o combos a la comanda activa.
        </p>

        <div className="mb-3 flex items-stretch gap-2">
          <div className="min-w-0 flex-1">
            <SearchInputField
              name="menuSearch"
              value={search}
              placeholder="Buscar por nombre o código..."
              withMargin={false}
              size="sm"
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </div>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setFilterOpen((open) => !open)}
              aria-expanded={filterOpen}
              className={`flex h-full items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition ${
                filterOpen || activeFilterCount > 0
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-stroke bg-white text-body hover:border-primary hover:text-primary dark:border-strokedark dark:bg-boxdark dark:text-bodydark'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtrar
              {activeFilterCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setFilterOpen(false)}
                />
                <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-black dark:text-white">
                      Filtros
                    </span>
                    <button
                      type="button"
                      aria-label="Cerrar filtros"
                      onClick={() => setFilterOpen(false)}
                      className="rounded-lg p-1 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {categories.length > 0 && (
                    <div className="mb-3">
                      <CompactSearchSelect
                        label="Categoría"
                        name="menuCategory"
                        withMargin={false}
                        value={selectedCategoryId}
                        searchPlaceholder="Buscar categoría..."
                        options={[
                          { value: 'ALL', label: 'Todas' },
                          ...categories.map((category) => ({
                            value: category.id,
                            label: category.name,
                          })),
                        ]}
                        onChange={(event) =>
                          onCategoryChange(
                            event.target.value === 'ALL'
                              ? 'ALL'
                              : Number(event.target.value)
                          )
                        }
                      />
                    </div>
                  )}

                  <SelectField
                    label="Disponibilidad"
                    name="menuAvailability"
                    value={filters.availability}
                    withMargin={false}
                    options={AVAILABILITY_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    onChange={(event) =>
                      patchFilters({
                        availability: String(
                          event.target.value
                        ) as RestaurantMenuAvailabilityFilter,
                      })
                    }
                  />

                  <div className="mt-3">
                    <Checkbox
                      id="menu-filter-featured"
                      label="Solo destacados"
                      checked={filters.featuredOnly}
                      onChange={() =>
                        patchFilters({ featuredOnly: !filters.featuredOnly })
                      }
                    />
                  </div>

                  <p className="mb-1.5 mt-3 text-xs font-medium text-black dark:text-white">
                    Precio
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <CompactInputField
                      label="Desde"
                      name="menuPriceMin"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      inputMode="decimal"
                      value={filters.priceMin}
                      onChange={(event) =>
                        patchFilters({ priceMin: event.target.value })
                      }
                    />
                    <CompactInputField
                      label="Hasta"
                      name="menuPriceMax"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      inputMode="decimal"
                      value={filters.priceMax}
                      onChange={(event) =>
                        patchFilters({ priceMax: event.target.value })
                      }
                    />
                  </div>

                  <div className="mt-4">
                    <ActionButton
                      label="Limpiar filtros"
                      variant="outline"
                      size="sm"
                      fullWidth
                      disabled={activeFilterCount === 0}
                      onClick={() => onFiltersChange(EMPTY_MENU_FILTERS)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Categorías anteriores"
            onClick={() => scrollStrip(-1)}
            className="shrink-0 rounded-full border border-stroke p-1 text-bodydark2 transition hover:border-primary hover:text-primary dark:border-strokedark"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div
            ref={stripRef}
            className="flex flex-1 gap-2 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button
              type="button"
              onClick={() => onCategoryChange('ALL')}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                selectedCategoryId === 'ALL'
                  ? 'bg-primary text-white'
                  : 'border border-transparent bg-gray-2 text-body hover:border-primary/40 hover:bg-primary/10 hover:text-primary active:scale-95 dark:bg-meta-4 dark:text-bodydark dark:hover:bg-primary/20'
              }`}
            >
              Todo
            </button>

            {categories.map((category) => {
              const active = selectedCategoryId === category.id;

              return (
                <button
                  type="button"
                  key={category.id}
                  onClick={() => onCategoryChange(category.id)}
                  style={
                    active && category.color
                      ? { backgroundColor: category.color }
                      : undefined
                  }
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? 'bg-primary text-white'
                      : 'border border-transparent bg-gray-2 text-body hover:border-primary/40 hover:bg-primary/10 hover:text-primary active:scale-95 dark:bg-meta-4 dark:text-bodydark dark:hover:bg-primary/20'
                  }`}
                >
                  {category.icon && (
                    <InputIconByKey
                      iconKey={category.icon as IconKey}
                      className={`h-3.5 w-3.5 shrink-0 ${
                        active ? 'text-white' : ''
                      }`}
                    />
                  )}
                  {category.name}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            aria-label="Categorías siguientes"
            onClick={() => scrollStrip(1)}
            className="shrink-0 rounded-full border border-stroke p-1 text-bodydark2 transition hover:border-primary hover:text-primary dark:border-strokedark"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-3 p-4 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]">
        {menuLoading && (
          <div className="col-span-full flex items-center justify-center gap-2 py-6 text-sm text-bodydark2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Aplicando filtros...
          </div>
        )}

        {menuItems.map((item) => (
          <button
            type="button"
            key={item.id}
            disabled={!canEdit || busy}
            onClick={() => onChooseItem(item)}
            className="group flex flex-col overflow-hidden rounded-2xl border border-stroke bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:bg-boxdark"
          >
            <div className="relative aspect-[5/3] overflow-hidden bg-gray-2 dark:bg-meta-4">
              <RestaurantMenuItemImage
                path={item.effectiveImagePath}
                alt={item.displayName || item.productName}
                className="absolute inset-0 h-full w-full"
              />
              {item.featured && (
                <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-warning px-2 py-0.5 text-[9px] font-bold uppercase text-white shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  Destacado
                </span>
              )}
            </div>

            <div className="flex flex-1 flex-col p-3">
              <h3 className="line-clamp-2 text-sm font-semibold text-black dark:text-white">
                {item.displayName || item.productName}
              </h3>
              {item.categoryName && (
                <span className="mt-1 w-fit rounded-full bg-gray-2 px-2 py-0.5 text-[10px] font-medium text-body dark:bg-meta-4 dark:text-bodydark1">
                  {item.categoryName}
                </span>
              )}

              <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                <strong className="text-base font-bold text-primary">
                  {formatOrderMoney(item.productPrice)}
                </strong>
                <span className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary transition-all group-hover:bg-primary group-hover:text-white">
                  <Plus className="h-3.5 w-3.5" />
                  Agregar
                </span>
              </div>
            </div>
          </button>
        ))}

        {combos.map((combo) => (
          <button
            type="button"
            key={`combo-${combo.id}`}
            disabled={!canEdit || busy}
            onClick={() => onChooseCombo(combo)}
            className="group flex flex-col overflow-hidden rounded-2xl border border-primary/40 bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 dark:bg-boxdark"
          >
            <div className="relative flex aspect-[5/3] items-center justify-center bg-primary/10 text-primary">
              <ChefHat className="h-9 w-9" />
              <span className="absolute left-0 top-0 rounded-br-lg bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                Combo
              </span>
              <span className="absolute right-2 top-2 rounded-full bg-white/80 px-2 py-0.5 text-[9px] font-bold uppercase text-primary shadow-sm dark:bg-boxdark/80">
                Ahorro
              </span>
            </div>

            <div className="flex flex-1 flex-col p-3">
              <h3 className="line-clamp-2 text-sm font-semibold text-black dark:text-white">
                {combo.name}
              </h3>
              <p className="mt-1 line-clamp-2 text-[11px] text-bodydark2">
                {combo.description ||
                  combo.items
                    .map((item) => item.menuItemName)
                    .filter(Boolean)
                    .join(', ')}
              </p>

              <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                <strong className="text-base font-bold text-primary">
                  {formatOrderMoney(combo.price)}
                </strong>
                <span className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary transition-all group-hover:bg-primary group-hover:text-white">
                  <Plus className="h-3.5 w-3.5" />
                  Agregar
                </span>
              </div>
            </div>
          </button>
        ))}

        {!menuLoading && menuItems.length === 0 && combos.length === 0 && (
          <div className="col-span-full flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-stroke bg-gray-2/30 dark:border-strokedark dark:bg-meta-4/20">
            <ChefHat className="mb-4 h-10 w-10 text-bodydark2" />

            <p className="text-base font-medium text-black dark:text-white">
              No hay productos disponibles
            </p>

            <p className="mt-1 text-sm text-bodydark2">
              {search.trim() || activeFilterCount > 0
                ? 'Ningún plato o combo coincide con la búsqueda o los filtros.'
                : 'No se encontraron platos o combos para esta categoría.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderMenuPanel;
